import axios from 'axios';
import { playAlarmSound, playButtonAlertStreamAudioDetailed } from './alarmSound';
import { notificationPayloadToNum } from './notificationPayload';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

const STREAM_AUDIO_POLL_MS = 700;
const STREAM_AUDIO_POLL_MAX = 60;

export function isStreamFinalizedNotification(rec: Record<string, unknown>): boolean {
  return (
    rec.streamFinalized === true ||
    rec.stream_finalized === true ||
    rec.streamAudioReady === true
  );
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const t = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(t);
      signal?.removeEventListener('abort', onAbort);
      resolve();
    };
    signal?.addEventListener('abort', onAbort);
  });
}

export type AlertRow = {
  audioPath?: string | null;
  metadata?: Record<string, unknown> | null;
};

export function isStreamPersonalizedSound(sound?: number | null): boolean {
  return sound === 14;
}

/** Resuelve sonido 14 desde BD si el socket no lo trajo. */
export async function fetchButtonAlertSound(
  buttonAlertId: number,
  token: string,
  signal?: AbortSignal
): Promise<number | undefined> {
  const res = await axios.get<{ success?: boolean; data?: AlertRow }>(
    `${API_URL}/button-alerts/${buttonAlertId}`,
    { headers: { Authorization: `Bearer ${token}` }, signal }
  );
  const row = res.data?.data;
  const meta = row?.metadata;
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return undefined;
  const s = notificationPayloadToNum(meta.sound);
  if (s === 14) return 14;
  if (meta.stream_id != null && String(meta.stream_id).trim() !== '') return 14;
  if (meta.stream_finalized === true) return 14;
  return s;
}

export function streamAudioReady(row: AlertRow | null | undefined): boolean {
  if (!row || row.audioPath == null || String(row.audioPath).trim() === '') {
    return false;
  }
  const meta = row.metadata;
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    if (meta.stream_finalized === true) return true;
  }
  return false;
}

/** Marca única del MP3 actual de la alerta (metadata.stream_audio_version). */
export function streamAudioVersionFromRow(
  row: AlertRow | null | undefined
): number | undefined {
  const meta = row?.metadata;
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return undefined;
  return notificationPayloadToNum((meta as Record<string, unknown>).stream_audio_version);
}

/**
 * Espera a que el backend guarde el MP3 del stream (sonido 14) y lo reproduce en el VMS.
 * Si no está listo a tiempo, no reproduce nada (sin respaldo genérico).
 * @returns true si se reprodujo el MP3 del stream.
 */
export async function pollAndPlayButtonAlertStreamAudio(
  buttonAlertId: number,
  token: string,
  signal?: AbortSignal,
  options?: {
    force?: boolean;
    /** true si esa versión de MP3 ya se reprodujo (para no repetir el mismo audio). */
    isVersionPlayed?: (version: number | undefined) => boolean;
    /** Registrar la versión reproducida tras un play() exitoso. */
    onPlayed?: (version: number | undefined) => void;
  }
): Promise<boolean> {
  const force = options?.force === true;
  for (let i = 0; i < STREAM_AUDIO_POLL_MAX; i++) {
    if (signal?.aborted) return false;
    try {
      const res = await axios.get<{ success?: boolean; data?: AlertRow }>(
        `${API_URL}/button-alerts/${buttonAlertId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        }
      );
      const row = res.data?.data;
      if (streamAudioReady(row)) {
        const version = streamAudioVersionFromRow(row);
        // El MP3 listo es uno que ya sonó: seguir esperando por un audio nuevo
        // (misma alerta reutilizada) en vez de repetir el actual.
        if (options?.isVersionPlayed?.(version)) {
          await sleep(STREAM_AUDIO_POLL_MS, signal);
          continue;
        }
        const played = await playButtonAlertStreamAudioDetailed(buttonAlertId, { force });
        if (played.ok) {
          options?.onPlayed?.(version);
          return true;
        }
        console.warn('[ButtonAlertStream] stream ready but play() failed, id=', buttonAlertId);
        // Otro stream suena: no caer al genérico ni seguir spameando.
        if (!force) return false;
        // 404 puntual tras "ready": esperar y re-chequear metadata (no hammer /audio).
        await sleep(STREAM_AUDIO_POLL_MS, signal);
        continue;
      }
    } catch (e) {
      if (axios.isCancel(e) || signal?.aborted) return false;
    }
    await sleep(STREAM_AUDIO_POLL_MS, signal);
  }
  // Timeout sin MP3 del stream: no reproducir nada (sin respaldo genérico).
  return false;
}

export type ReplayButtonAlertAudioResult =
  | { ok: true }
  | { ok: false; reason: 'not_found' | 'unauthorized' | 'empty' | 'play_failed' | 'busy' | 'no_token' };

/**
 * Reproduce el audio de una alerta a demanda (p. ej. icono speaker).
 * Stream (14): fuerza el MP3 vía API aunque ya haya sonado.
 * Pregrabados 1–5: vuelve a lanzar el archivo de /alarms.
 */
export async function replayButtonAlertAudio(
  buttonAlertId: number,
  token: string | null,
  soundHint?: number | null
): Promise<ReplayButtonAlertAudioResult> {
  if (!Number.isFinite(buttonAlertId) || buttonAlertId <= 0) {
    return { ok: false, reason: 'not_found' };
  }

  let sound =
    soundHint != null && Number.isFinite(soundHint) ? Number(soundHint) : undefined;

  if (sound === undefined && token) {
    try {
      sound = await fetchButtonAlertSound(buttonAlertId, token);
    } catch {
      /* ignore */
    }
  }

  if (isStreamPersonalizedSound(sound)) {
    if (!token) return { ok: false, reason: 'no_token' };
    return playButtonAlertStreamAudioDetailed(buttonAlertId, { force: true });
  }

  if (sound != null && sound >= 1 && sound <= 5) {
    playAlarmSound(sound);
    return { ok: true };
  }

  // Sin hint claro: intentar MP3 de stream (alerta personalizada sin sound en payload).
  if (token) {
    return playButtonAlertStreamAudioDetailed(buttonAlertId, { force: true });
  }
  return { ok: false, reason: 'no_token' };
}
